import { SelectOption } from 'src/app/components'
import { MallModel, StoreCategoryModel, StoreModel } from '../../directory/models'
import { CampaignModel } from './Campaign.model'
import { PaymentCategoryModel } from '../../settings/models/PaymentCategory.model'
import { PaymentMethodModel } from '../../settings/models/PaymentMethod.model'

export const PRINT_DATE_TYPE_LABELS = {
  MAX_DAY_TO_PRINT: 'Max Days to Print After Receipt Approved',
  FIX_DATE: 'Last Print Date',
} as const

export const PRINT_DATE_TYPE_OPTIONS: { label: string; value: 'MAX_DAY_TO_PRINT' | 'FIX_DATE' }[] = [
  { label: PRINT_DATE_TYPE_LABELS.MAX_DAY_TO_PRINT, value: 'MAX_DAY_TO_PRINT' },
  { label: PRINT_DATE_TYPE_LABELS.FIX_DATE, value: 'FIX_DATE' },
]

export function resolvePrintDateType(printDateType: unknown): 'MAX_DAY_TO_PRINT' | 'FIX_DATE' | undefined {
  if (printDateType && typeof printDateType === 'object' && 'value' in (printDateType as object)) {
    return resolvePrintDateType((printDateType as { value: unknown }).value)
  }
  if (printDateType === 'MAX_DAY_TO_PRINT' || printDateType === 'FIX_DATE') {
    return printDateType
  }
  return undefined
}

export function getPrintDateTypeOption(printDateType: unknown) {
  const value = resolvePrintDateType(printDateType)
  return PRINT_DATE_TYPE_OPTIONS.find((option) => option.value === value)
}

type PrintDateTypeSource = {
  print_date_type?: unknown
  max_day_to_print?: number | null
  last_print_date?: string | null
}

export function inferPrintDateType(source?: PrintDateTypeSource | null): 'MAX_DAY_TO_PRINT' | 'FIX_DATE' | undefined {
  const resolved = resolvePrintDateType(source?.print_date_type)
  if (resolved) return resolved
  if (source?.max_day_to_print != null && Number(source.max_day_to_print) > 0) {
    return 'MAX_DAY_TO_PRINT'
  }
  if (source?.last_print_date) return 'FIX_DATE'
  return undefined
}

export function getPrintDateTypeFormValue(source?: PrintDateTypeSource | null) {
  return getPrintDateTypeOption(inferPrintDateType(source))
}

export function restorePrintDateTypeFormValue(
  current?: unknown,
  source?: PrintDateTypeSource | null,
) {
  return getPrintDateTypeOption(current) ?? getPrintDateTypeFormValue(source)
}

export function validatePrintCoupon(value: unknown) {
  if (value === true || value === false || value === 'true' || value === 'false') {
    return true
  }
  return 'Field cannot be empty'
}

export function isPrintCouponYes(value: unknown) {
  return value === true || value === 'true'
}

export function validatePrintDateTypeField(value: unknown, isPrintable: unknown) {
  if (!isPrintCouponYes(isPrintable)) return true
  return !!resolvePrintDateType(value) || 'Field cannot be empty'
}

export function isPrintDateTypeLockedOnEdit(
  campaignStatus?: string,
  initialPrintDateType?: 'MAX_DAY_TO_PRINT' | 'FIX_DATE',
) {
  return ['DRAFT', 'PENDING', 'ACTIVE'].includes(campaignStatus ?? '') && initialPrintDateType === 'FIX_DATE'
}

export function mergeLuckyDrawGeneralSource(
  currentDetail?: LuckyDrawModel,
  queryData?: LuckyDrawModel,
): LuckyDrawModel | undefined {
  if (!queryData && !currentDetail) return undefined

  const merged: LuckyDrawModel = { ...currentDetail, ...queryData }
  const printDateType = inferPrintDateType({
    print_date_type:
      resolvePrintDateType(queryData?.print_date_type) ??
      resolvePrintDateType(currentDetail?.print_date_type),
    max_day_to_print: queryData?.max_day_to_print ?? currentDetail?.max_day_to_print,
    last_print_date: queryData?.last_print_date || currentDetail?.last_print_date,
  })

  if (printDateType) {
    merged.print_date_type = printDateType
  }

  if (printDateType === 'FIX_DATE') {
    merged.last_print_date = queryData?.last_print_date || currentDetail?.last_print_date
  } else if (printDateType === 'MAX_DAY_TO_PRINT') {
    merged.max_day_to_print = queryData?.max_day_to_print ?? currentDetail?.max_day_to_print
    delete merged.last_print_date
  }

  return merged
}

export function getPrintDateTypeLabel(printDateType: unknown, hasLastPrintDate?: boolean) {
  const value = resolvePrintDateType(printDateType)
  if (value === 'MAX_DAY_TO_PRINT') return PRINT_DATE_TYPE_LABELS.MAX_DAY_TO_PRINT
  if (value === 'FIX_DATE' || hasLastPrintDate) return PRINT_DATE_TYPE_LABELS.FIX_DATE
  return '-'
}

export interface LuckyDrawModel extends CampaignModel {
  category?: string
  name?: string
  category_type?: SelectOption
  campaign_name?: string
  directory_ids?: (string | undefined)[]
  directories?: MallModel[]
  is_printable?: boolean
  print_date_type?: 'MAX_DAY_TO_PRINT' | 'FIX_DATE'
  max_day_to_print?: number
  last_print_date?: string
  coupon_selection_image?: string
  max_day_to_choose_prize?: number
  image?: string
  thumbnail_image?: string
  tnc_eng?: string
  tnc_idn?: string
  is_draft?: boolean
  consolation_alias?: string
}

export interface PrizeListModel {
  id?: string
  campaign_id?: string
  draw_directory_id?: string
  name?: string
  type?: string
  prize_type?: string
  description_idn?: string
  description_eng?: string
  image?: string
  quantity?: number
  created_at?: string
  updated_at?: string
  directory?: MallModel
  draw_directory?: MallModel
  draw_date?: string
  last_print_date?: string
}

export interface LuckyDrawRuleMasterModel {
  prize_rules: LuckyDrawRuleModel[]
}

export interface LuckyDrawRuleModel {
  id?: string
  name?: string
  merchant_category_ids?: string[]
  merchant_ids?: string[]
  merchants?: StoreModel[]
  merchant_categories?: StoreCategoryModel[]
  prize_transaction_rules?: TransactionRule[]
}

export interface LuckyDrawDetailModel extends LuckyDrawModel {
  prizes?: PrizeListModel[]
  prize_rules?: LuckyDrawRuleModel[]
}

export interface TransactionRule {
  id?: string
  name?: string
  minimum_transaction?: number | null
  minimum_transaction_per_receipt?: number
  payment_category_ids?: string[]
  payment_method_ids?: string[]
  prizes?: PrizeRuleDetail[]
  payment_categories?: PaymentCategoryModel[]
  payment_methods?: PaymentMethodModel[]
  is_mixable?: boolean
  is_multiple_entry?: boolean
  is_combine_receipt?: boolean
  max_combine_receipt?: number | null
  min_combine_total_amount?: number | null
  is_multiplication_combine_receipt?: boolean
  days?: string[]
  start_time?: string
  end_time?: string
}

export interface PrizeRuleDetail {
  id?: string
  prize_ids?: string[] // Optional because "CONSOLATION" prize has no prize_ids
  prizes?: PrizeListModel | PrizeListModel[]
  type?: 'GRAND_PRIZE' | 'CONSOLATION' // Can extend if more types exist
  coupon_quantity?: number
  max_coupon_per_transaction?: number
  consolation_alias?: string
}

export interface LuckyDrawParticipantModel {
  id?: string
  member_code?: string
  fullname?: string
  status?: 'DELETED' | 'ACTIVE' | 'SUSPEND'
  grand_prize_coupon_count?: string
  consolation_coupon_count?: string
}

export interface ParticipantSummaryModel {
  total_participant?: string
  total_claimed_coupon?: string
  total_printed_coupon?: string
  is_printable?: boolean
}

export interface ParticipantDetailModel extends LuckyDrawParticipantModel {
  gender?: string
  phone_number?: string
  email?: string
  identity_type?: string
  identity_image?: string
  identity_status?: string //VERIFIED / UNVERIFIED / NOT_UPLOADED / REJECTED
  total_coupon?: number
  total_coupon_printed?: number
  total_valid_coupon?: number
  is_unprinted_consolation_exist?: boolean
  is_unprinted_exist?: boolean
  identity_number?: string
  id?: string
  is_printable?: boolean
}

export interface ParticipantCouponModel {
  id?: string
  coupon_code?: string
  transaction_code?: string
  created_at?: string
  is_printed?: boolean
  printed_at?: string
  reprinted_at?: string
  print_directory_name?: string
  coupon_printed_code?: string
  status?: string // VALID / INVALID / EXPIRED
  winning_status?: string // WON / LOST / WAITING
  receipt_code?: string
  failure_reason?: string
  campaign_name?: string
  start_date?: string
  end_date?: string
  prize_name?: string // if type Consolation, using consolation_alias
  consolation_alias?: string
  fullname?: string
  member_code?: string
}

export interface CampaignWinnerHistory {
  id?: string
  member_id?: string
  member_coupon_id?: string
  campaign_id?: string
  campaign?: LuckyDrawModel
  prize_id?: string
  prize?: PrizeListModel
  draw_date?: string // "YYYY-MM-DD"
  draw_directory_id?: string
  draw_directory?: MallModel
}

export interface CouponPrintedHistory {
  id?: string
  member_coupon_id?: string
  coupon_printed_code?: string
  note?: string
  created_at?: string
  user_id?: string
  user?: {
    id?: string
    name?: string
  }
}

export interface DrawingModel {
  id?: string
  campaign_id?: string
  coupon_code?: string
  coupon_printed_code?: string
  print_directory_id?: string
  print_directory?: MallModel
  draw_directory_id?: string
  draw_directory?: MallModel
  prize_id?: string
  prize?: PrizeListModel
  prize_type?: string
  member_id?: string
  member?: ParticipantDetailModel
  remarks?: string[]
  can_set_as_winner?: boolean
  campaign_winner_histories?: CampaignWinnerHistory[]
  coupon_printed_histories?: CouponPrintedHistory[]
  status?: string
  receipt_code?: string
  is_printable?: boolean
}

export interface WinnerModel {
  id?: string
  member_id?: string
  member?: ParticipantDetailModel
  draw_date?: string
  draw_directory_id?: string
  draw_directory?: MallModel
  prize_id?: string
  prize?: PrizeListModel
}

export interface WinnersModel {
  id?: string
  name?: string
  data?: WinnerModel[]
}